/**
 * DevPhaseTab — IN REVIEW stage — DOMAIN-ADAPTIVE
 * For biotech: phase timeline visualization (Basic Research → Preclinical → Phase I-III → Approved)
 * For other domains: simpler milestone/status view
 */

import type { ProjectDetailProject } from '../../../types'
import { CompletableSection } from '../../ui/CompletableSection'
import type { SectionCompletionState } from '../../../hooks/useSectionCompletions'

export interface TabContentProps {
  project: ProjectDetailProject
  projectId: number
  domain: string
  onFieldChange: (field: string, value: any) => void
  sectionCompletions?: SectionCompletionState
  tabId?: string
}

const BIOTECH_PHASES = [
  { id: 'basic-research', label: 'Basic Research' },
  { id: 'preclinical', label: 'Preclinical' },
  { id: 'phase-1', label: 'Phase I' },
  { id: 'phase-2', label: 'Phase II' },
  { id: 'phase-3', label: 'Phase III' },
  { id: 'approved', label: 'Approved' },
]

export function DevPhaseTab({
  project,
  projectId,
  domain,
  onFieldChange,
  sectionCompletions,
  tabId,
}: TabContentProps) {
  if (domain === 'biotech') {
    return (
      <BiotechPhaseView
        project={project}
        onFieldChange={onFieldChange}
        sectionCompletions={sectionCompletions}
        tabId={tabId}
      />
    )
  } else {
    return (
      <GenericPhaseView
        project={project}
        domain={domain}
        onFieldChange={onFieldChange}
        sectionCompletions={sectionCompletions}
        tabId={tabId}
      />
    )
  }
}

/**
 * Biotech-specific phase timeline
 */
function BiotechPhaseView({
  project,
  onFieldChange,
  sectionCompletions,
  tabId,
}: {
  project: ProjectDetailProject
  onFieldChange: (field: string, value: any) => void
  sectionCompletions?: SectionCompletionState
  tabId?: string
}) {
  const sc = sectionCompletions
  const isComplete = (sectionId: string) => sc?.isSectionComplete(tabId || '', sectionId) ?? false
  const getInfo = (sectionId: string) => sc?.getCompletionInfo(tabId || '', sectionId)
  const toggle = (sectionId: string) => sc?.toggleSection(tabId || '', sectionId)

  const currentPhase = project.dev_phase || 'preclinical'

  const getPhaseIndex = (phase: string) => {
    return BIOTECH_PHASES.findIndex((p) => p.id === phase)
  }

  const currentIndex = getPhaseIndex(currentPhase)

  return (
    <div className="space-y-6">
      {/* Phase Timeline */}
      <CompletableSection
        sectionId="dev-timeline"
        title="Development Phase Timeline"
        isComplete={isComplete('dev-timeline')}
        completedByName={getInfo('dev-timeline')?.completed_by_name}
        completedAt={getInfo('dev-timeline')?.completed_at}
        onToggle={() => toggle('dev-timeline')}
      >
        {/* Timeline visualization */}
        <div className="flex items-center gap-2 mb-8">
          {BIOTECH_PHASES.map((phase, idx) => {
            const isPassed = idx < currentIndex
            const isCurrent = idx === currentIndex
            const isFuture = idx > currentIndex

            return (
              <div key={phase.id} className="flex items-center flex-1">
                {/* Phase button */}
                <button
                  onClick={() => onFieldChange('dev_phase', phase.id)}
                  className={`
                    flex-1 px-3 py-2 rounded-lg
                    text-center text-[12px] font-medium
                    transition-colors
                    ${
                      isCurrent
                        ? 'bg-[#E61B25] text-white'
                        : isPassed
                        ? 'bg-[#10B981] text-white'
                        : 'bg-[#12122a] border border-[#2a2a44] text-[#a8a8c0]'
                    }
                  `}
                >
                  {isPassed && '✓ '}
                  {phase.label}
                </button>

                {/* Connector (except after last phase) */}
                {idx < BIOTECH_PHASES.length - 1 && (
                  <div
                    className={`
                      w-2 h-1 mx-2
                      ${isPassed ? 'bg-[#10B981]' : 'bg-[#2a2a44]'}
                    `}
                  />
                )}
              </div>
            )
          })}
        </div>

        {/* Phase details */}
        <div className="bg-[#12122a] border border-[#2a2a44] rounded-lg p-4">
          <div className="text-[13px] text-[#e8e8f0]">
            <strong>Current Phase:</strong>{' '}
            {BIOTECH_PHASES.find((p) => p.id === currentPhase)?.label}
          </div>
          <div className="text-[12px] text-[#a8a8c0] mt-2">
            Click on a phase to update the development stage
          </div>
        </div>
      </CompletableSection>

      {/* Phase-specific details */}
      <CompletableSection
        sectionId="dev-details"
        title="Phase Details"
        isComplete={isComplete('dev-details')}
        completedByName={getInfo('dev-details')?.completed_by_name}
        completedAt={getInfo('dev-details')?.completed_at}
        onToggle={() => toggle('dev-details')}
      >
        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Phase Start Date
          </label>
          <input
            type="date"
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54]
            "
          />
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Estimated Phase Duration
          </label>
          <input
            type="text"
            placeholder="e.g. 2-3 years"
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54]
            "
          />
        </div>

        <div>
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Key Milestones
          </label>
          <input
            type="text"
            placeholder="Upcoming milestones or deliverables"
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54]
            "
          />
        </div>
      </CompletableSection>

      {/* Development data */}
      <CompletableSection
        sectionId="dev-data"
        title="Development Data"
        isComplete={isComplete('dev-data')}
        completedByName={getInfo('dev-data')?.completed_by_name}
        completedAt={getInfo('dev-data')?.completed_at}
        onToggle={() => toggle('dev-data')}
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#12122a] border border-[#2a2a44] rounded-lg p-3">
            <div className="text-[12px] text-[#a8a8c0] mb-1">Publications</div>
            <div className="text-[15px] font-medium text-[#e8e8f0]">
              {project.publications?.length || 0}
            </div>
          </div>

          <div className="bg-[#12122a] border border-[#2a2a44] rounded-lg p-3">
            <div className="text-[12px] text-[#a8a8c0] mb-1">Patents</div>
            <div className="text-[15px] font-medium text-[#e8e8f0]">
              {project.patent_count || 0}
            </div>
          </div>

          <div className="bg-[#12122a] border border-[#2a2a44] rounded-lg p-3">
            <div className="text-[12px] text-[#a8a8c0] mb-1">Citations</div>
            <div className="text-[15px] font-medium text-[#e8e8f0]">
              {project.citation_count || 0}
            </div>
          </div>

          <div className="bg-[#12122a] border border-[#2a2a44] rounded-lg p-3">
            <div className="text-[12px] text-[#a8a8c0] mb-1">H-index</div>
            <div className="text-[15px] font-medium text-[#e8e8f0]">
              {project.h_index || 'N/A'}
            </div>
          </div>
        </div>
      </CompletableSection>
    </div>
  )
}

/**
 * Generic phase view for non-biotech domains
 */
function GenericPhaseView({
  project,
  domain,
  onFieldChange,
  sectionCompletions,
  tabId,
}: {
  project: ProjectDetailProject
  domain: string
  onFieldChange: (field: string, value: any) => void
  sectionCompletions?: SectionCompletionState
  tabId?: string
}) {
  const sc = sectionCompletions
  const isComplete = (sectionId: string) => sc?.isSectionComplete(tabId || '', sectionId) ?? false
  const getInfo = (sectionId: string) => sc?.getCompletionInfo(tabId || '', sectionId)
  const toggle = (sectionId: string) => sc?.toggleSection(tabId || '', sectionId)

  const GENERIC_MILESTONES = [
    'Concept',
    'Prototype',
    'MVP',
    'Beta',
    'Launch',
    'Scaling',
  ]

  return (
    <div className="space-y-6">
      <CompletableSection
        sectionId="dev-timeline"
        title="Development Stage"
        isComplete={isComplete('dev-timeline')}
        completedByName={getInfo('dev-timeline')?.completed_by_name}
        completedAt={getInfo('dev-timeline')?.completed_at}
        onToggle={() => toggle('dev-timeline')}
      >
        <div className="mb-6">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-3">
            Current Stage
          </label>
          <select
            value={project.dev_phase || ''}
            onChange={(e) => onFieldChange('dev_phase', e.target.value)}
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54]
            "
          >
            <option value="">Select stage</option>
            {GENERIC_MILESTONES.map((milestone) => (
              <option key={milestone} value={milestone}>
                {milestone}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Stage Start Date
          </label>
          <input
            type="date"
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              focus:border-[#3a3a54]
            "
          />
        </div>

        <div>
          <label className="text-[13px] font-medium text-[#e8e8f0] block mb-2">
            Key Milestones Ahead
          </label>
          <input
            type="text"
            placeholder="Next major milestone or deadline"
            className="
              w-full px-3 py-2
              bg-[#12122a] border border-[#2a2a44]
              rounded-lg
              text-[#e8e8f0] text-[13px]
              placeholder:text-[#5a5a6c]
              focus:border-[#3a3a54]
            "
          />
        </div>
      </CompletableSection>
    </div>
  )
}

export default DevPhaseTab
